import React, { CSSProperties, Fragment } from "react";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell, { TableCellProps } from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Error } from "@mui/icons-material";
import { TABLE_BORDER_GRAY } from "../../../../constants/css.constants";
import { Link } from "react-router-dom";
import { ROUTES } from "../../../../routes";
import {
  COUNTRY_PARAM,
  TERRITORY_PARAM,
} from "../../../../hooks/useQueryParams";
import {
  COUNTRIES,
  COUNTRY_TO_TERRITORY,
} from "../../../LeftNav/config/Filters";
import {
  BE_TO_FE_VALUES,
  FE_TO_BE_VALUES,
} from "../../../../constants/app.constants";

const SHIPMENT_PAGE_HEADERS = ["FW", "AP", "EQ", "TB"] as const;
type SHIPMENT_HEADER_TYPE = (typeof SHIPMENT_PAGE_HEADERS)[number];
const HEADERS = ["Overall Status", "Channel", "Business Model"];

const ROW_TO_PAGE_MAPPING = {
  "3PW": {
    [BE_TO_FE_VALUES.NMP]: ROUTES.NMP,
    [BE_TO_FE_VALUES.NSP]: ROUTES.NSP,
    [BE_TO_FE_VALUES.NMP_PRICE]: ROUTES.NMP_WHOLESALE,
    [BE_TO_FE_VALUES.NSP_PRICE]: ROUTES.NSP_WHOLESALE,
  },
  NSO: {
    [BE_TO_FE_VALUES.NSO_OWNED]: ROUTES.STORE_OWNED,
    [BE_TO_FE_VALUES.NSP_HYBRID]: ROUTES.STORE_HYBRID,
  },
  NVS: {
    [BE_TO_FE_VALUES.NVS]: ROUTES.VALUE_STORE,
  },
  NDDC: {
    [BE_TO_FE_VALUES["NIKE.COM"]]: ROUTES.NIKE_DOT_COM,
    [BE_TO_FE_VALUES["3PP_CONSIGN"]]: ROUTES.CONSIGNMENT,
    [BE_TO_FE_VALUES["3PP_DROP_SHIP"]]: ROUTES.DROP_SHIP,
  },
};
interface StyledCellProps extends TableCellProps {
  cssProperties?: CSSProperties;
  index?: number;
}
export const StyledTableCell = React.forwardRef(
  (
    { children, cssProperties = {}, index = 1, ...props }: StyledCellProps,
    ref,
  ) => {
    const additionalCSSProperties =
      index === 0
        ? { borderLeft: TABLE_BORDER_GRAY, ...cssProperties }
        : cssProperties;
    return (
      <TableCell
        sx={{
          borderRight: TABLE_BORDER_GRAY,
          backgroundColor: "rgb(39, 41, 61)",
          minWidth: "100px",
          ...additionalCSSProperties,
        }}
        ref={ref}
        align="center"
        {...props}
      >
        {children}
      </TableCell>
    );
  },
);

type subStatuses = { [k in SHIPMENT_HEADER_TYPE]: boolean };
type SummaryGridProps = {
  incomingStatuses: {
    [k: string]: subStatuses | { [key: string]: subStatuses };
  };
  country: string;
};
const SummaryGrid = ({ incomingStatuses, country }: SummaryGridProps) => {
  function addUnitsSuffixTo3wpRows(businessModel: string) {
    if (businessModel === BE_TO_FE_VALUES.NMP || businessModel === BE_TO_FE_VALUES.NSP) {
      return businessModel + " - Units";
    }

    return businessModel;
  }

  const getIcon = (isComplete: boolean) =>
    isComplete ? (
      <CheckCircleIcon color="success" />
    ) : (
      <Error color="warning" />
    );
  const tree = COUNTRIES[country.toUpperCase()];
  if ('3PW' in tree){
    if ('Nike Marketplace Partners' in tree['3PW']){
      tree['3PW']["Nike Marketplace Partners - Whlse Amt"] = "Nike Marketplace Partners - Whlse Amt"
    }
    if ('Nike Store Partners' in tree['3PW']){
      tree['3PW']["Nike Store Partners - Whlse Amt"] = "Nike Store Partners - Whlse Amt"
    }
  }


  const statuses = Object.keys(incomingStatuses).reduce((acc, key) => {
    const value = incomingStatuses[key];
    const frontEndKey = BE_TO_FE_VALUES[key];
    if (
      value.FW !== undefined &&
      frontEndKey !== undefined &&
      !frontEndKey.includes("3PP")
    ) {
      return {
        ...acc,
        [frontEndKey]: value,
      };
    }
    if (frontEndKey === undefined) return acc;
    const subValues = Object.keys(value)
      .filter((subKey) => BE_TO_FE_VALUES[subKey])
      .reduce((subAcc, subKey) => {
        const subValue = value[subKey];
        return {
          ...subAcc,
          [BE_TO_FE_VALUES[subKey]]: subValue,
        };
      }, {});
    return {
      ...acc,
      [frontEndKey]: subValues,
    };
  }, {});

  let hasAccount = false;
  const buildRows = (tree: typeof COUNTRIES) => {
    return Object.keys(tree).map((key) => {
      return {
        channel: key,
        businessModels: Object.keys(tree[key]).map((businessKey) => {
          const value = tree[key][businessKey];
          if (typeof value === "string") {
            return {
              name: addUnitsSuffixTo3wpRows(businessKey),
              value: tree[key][businessKey],
              statuses: statuses[value] as subStatuses,
            };
          } else {
            const accounts = Object.keys(value)
              .filter(
                (account) =>
                  statuses[businessKey] && statuses[businessKey][account],
              )
              .map((account) => ({
                name: account,
                value: value[account],
                statuses: statuses[businessKey][account] as subStatuses,
              }));
            hasAccount = accounts.length > 0;
            return {
              name: businessKey,
              value: businessKey,
              accounts,
            };
          }
        }),
      };
    });
  };

  const rows = buildRows(tree);
  const countryHeaders = hasAccount
    ? [...HEADERS, "Account", ...SHIPMENT_PAGE_HEADERS]
    : [...HEADERS, ...SHIPMENT_PAGE_HEADERS];

  const getLinks = (
    showAllStatuses: boolean,
    channel: string,
    value: string,
    statuses: subStatuses | {},
    accountName: string,
  ) => {
    const accountLink = accountName.length
      ? COUNTRIES[country.toUpperCase()][channel][value][
          accountName
        ].toLowerCase()
      : "";
    return showAllStatuses ? (
      SHIPMENT_PAGE_HEADERS.map((productEngine) => (
        <StyledTableCell key={`${channel}-${value}-${productEngine}`}>
          <Link
            to={`${
              ROW_TO_PAGE_MAPPING[channel][value]
            }/${productEngine.toLowerCase()}?${TERRITORY_PARAM}=${COUNTRY_TO_TERRITORY[
              country.toUpperCase()
            ].replaceAll("&", "%26")}&${COUNTRY_PARAM}=${country}${
              accountName.length ? `&account=${accountLink}` : ""
            }`}
          >
            {getIcon(statuses[productEngine] ?? false)}
          </Link>
        </StyledTableCell>
      ))
    ) : (
      <StyledTableCell colSpan={SHIPMENT_PAGE_HEADERS.length} align="center">
        <Link
          to={`${ROW_TO_PAGE_MAPPING[channel][value]}/tb?${TERRITORY_PARAM}=${
            COUNTRY_TO_TERRITORY[country.toUpperCase()].replaceAll("&", "%26")
          }&${COUNTRY_PARAM}=${country}${
            accountName.length ? `&account=${accountLink}` : ""
          }`}
        >
          {getIcon(true)}
        </Link>
      </StyledTableCell>
    );
  };
  const createRow = (
    showAllStatuses: boolean,
    channel: string,
    name: string,
    value: string,
    statuses: subStatuses | {},
    accountName = "",
  ) => (
    <TableRow key={name}>
      <StyledTableCell>{name}</StyledTableCell>
      {hasAccount && (
        <StyledTableCell>
          {accountName.length ? accountName : "-"}
        </StyledTableCell>
      )}
      {getLinks(showAllStatuses, channel, value, statuses, accountName)}
    </TableRow>
  );

  const createAccountRow = (
    showAllStatuses: boolean,
    channel: string,
    name: string,
    value: string,
    accounts: { name: string; value: string; statuses: subStatuses }[],
  ) => {
    if (accounts.length === 1) {
      return createRow(
        showAllStatuses,
        channel,
        name,
        value,
        accounts[0].statuses,
        accounts[0].name,
      );
    }
    return (
      <>
        <TableRow key={name}>
          <StyledTableCell rowSpan={accounts.length + 1}>
            {name}
          </StyledTableCell>
        </TableRow>
        {accounts.map((account) => {
          const showAccountStatus = Object.keys(account.statuses).every(
            (status) => account.statuses[status],
          );
          return (
            <TableRow>
              {hasAccount && (
                <StyledTableCell>
                  {account.name.length ? account.name : "-"}
                </StyledTableCell>
              )}
              {getLinks(
                !showAccountStatus,
                channel,
                value,
                account.statuses,
                account.name,
              )}
            </TableRow>
          );
        })}
      </>
    );
  };

  return (
    <TableContainer>
      <TableHead>
        <TableRow>
          {countryHeaders.map((header, i) => {
            const cssProperties =
              (hasAccount && i < 4) || i < 3
                ? { backgroundColor: "#28282A" }
                : {};
            return (
              <StyledTableCell
                cssProperties={{
                  borderTop: TABLE_BORDER_GRAY,
                  ...cssProperties,
                }}
                index={i}
                key={header}
              >
                {header}
              </StyledTableCell>
            );
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => {
          const rowStatuses = row.businessModels.reduce(
            (acc, businessModel) => {
              const businessStatuses = statuses[businessModel.value];
              if (!businessStatuses) return acc;
              if (
                typeof businessStatuses === "object" &&
                businessStatuses["FW"] !== undefined
              ) {
                return {
                  ...acc,
                  [businessModel.value]: Object.keys(businessStatuses).every(
                    (key) => businessStatuses[key],
                  ),
                };
              }
              return {
                ...acc,
                [businessModel.value]: Object.keys(
                  statuses[businessModel.value],
                )
                  .filter((key) => businessStatuses[key] !== undefined)
                  .every((key) => {
                    const val = statuses[businessModel.value];
                    return Object.keys(val[key]).every((subkey) => {
                      const result = val[key][subkey];
                      return result;
                    });
                  }),
              };
            },
            {},
          );
          const isComplete = !Object.keys(rowStatuses).find(
            (key) => !rowStatuses[key],
          );
          const maxAccounts = Math.max(
            ...row.businessModels.map((businessModel) =>
              businessModel.accounts ? businessModel.accounts.length : 0,
            ),
          );
          const rowSpan =
            maxAccounts > 0
              ? maxAccounts + row.businessModels.length + 1
              : row.businessModels.length + 1;
          return (
            <Fragment key={row.channel}>
              <TableRow>
                <StyledTableCell rowSpan={rowSpan} index={0}>
                  {getIcon(isComplete)}
                </StyledTableCell>
                <StyledTableCell rowSpan={rowSpan}>
                  {row.channel}
                </StyledTableCell>
              </TableRow>
              {row.businessModels.map((businessModel) => {
                const showAllStatuses = !rowStatuses[businessModel.value];
                const hasAccounts =
                  businessModel.accounts && businessModel.accounts.length;
                return hasAccounts
                  ? createAccountRow(
                      showAllStatuses,
                      row.channel,
                      businessModel.name,
                      businessModel.value,
                      businessModel.accounts,
                    )
                  : createRow(
                      showAllStatuses,
                      row.channel,
                      businessModel.name,
                      businessModel.value,
                      businessModel.statuses ?? {},
                    );
              })}
            </Fragment>
          );
        })}
      </TableBody>
    </TableContainer>
  );
};
export default SummaryGrid;
